/**
 * SRC-20 shielded-transfer calldata builder.
 *
 * Selector is `0xb10c99b5` (`transfer(address,suint256)`). The standard ERC-20
 * `0xa9059cbb` is NOT compatible — see spec §14 foot-gun #2. Calldata layout
 * is `selector || abi.encode(recipient, amount)`.
 */
import type { Address, Hex } from 'viem'
import { concatHex, encodeAbiParameters } from 'viem'
import { SRC20_TRANSFER_SELECTOR } from '../constants'

export const buildSrc20TransferData = (recipient: Address, amount: bigint): Hex => {
  const encodedArgs = encodeAbiParameters([{ type: 'address' }, { type: 'uint256' }], [recipient, amount])
  return concatHex([SRC20_TRANSFER_SELECTOR, encodedArgs])
}

/**
 * Decode an SRC-20 transfer calldata back to `{ recipient, amount }` — used by
 * the transactions-queue label decoder to render "Shielded SRC-20 transfer of N <symbol>"
 * without the queue needing chain-specific knowledge.
 *
 * Returns `undefined` when the selector doesn't match or the payload is
 * shorter than expected.
 */
export const decodeSrc20TransferData = (data: Hex | undefined): { recipient: Address; amount: bigint } | undefined => {
  if (!data || data.length < 2 + 8 + 128) return undefined
  const selector = data.slice(0, 10).toLowerCase() as Hex
  if (selector !== SRC20_TRANSFER_SELECTOR) return undefined

  // Address: 32-byte word at offset 4..36, last 20 bytes is the address.
  const addrHex = ('0x' + data.slice(2 + 8 + 24, 2 + 8 + 64)) as Address
  const amountHex = ('0x' + data.slice(2 + 8 + 64, 2 + 8 + 128)) as Hex
  let amount = 0n
  try {
    amount = BigInt(amountHex)
  } catch {
    return undefined
  }
  return { recipient: addrHex, amount }
}
